import { useCallback } from 'react';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { InspoBrandView } from '@/sections/inspo/views/inspo-brand-view';
import { InspoPaywall } from '@/sections/inspo/components/inspo-paywall';
import { InspoWgRequestProvider } from '@/sections/inspo/components/inspo-wg-request';
import {
  type EmailFilterSearch,
  type EmailDetailSearch,
  parseEmailFilterSearch,
  parseEmailDetailSearch,
} from '@/sections/inspo/inspo-search';

// /dashboard/o/:orgId/c/:clientId/inspo/brands/:slug -- one brand's page: who
// they are, then every email of theirs in the archive, searchable on its own.

type BrandSearch = EmailFilterSearch & EmailDetailSearch;

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/inspo/brands/$slug')({
  component: InspoBrandPage,
  validateSearch: (search: Record<string, unknown>): BrandSearch => ({
    ...parseEmailFilterSearch(search),
    ...parseEmailDetailSearch(search),
  }),
});

function InspoBrandPage() {
  const { orgId, clientId, slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onSearchChange = useCallback(
    (patch: Partial<BrandSearch>, opts?: { replace?: boolean }) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: opts?.replace }),
    [navigate]
  );

  return (
    <InspoWgRequestProvider>
      <InspoPaywall>
        <InspoBrandView
          // Remounted per brand so the feed and search box start fresh when
          // moving from one brand to another through the detail dialog.
          key={slug}
          slug={slug}
          search={search}
          onSearchChange={onSearchChange}
          onBack={() =>
            void navigate({
              to: '/dashboard/o/$orgId/c/$clientId/inspo',
              params: { orgId, clientId },
              search: { tab: 'brands' },
            })
          }
          onOpenBrand={(next) =>
            void navigate({
              to: '/dashboard/o/$orgId/c/$clientId/inspo/brands/$slug',
              params: { orgId, clientId, slug: next },
            })
          }
        />
      </InspoPaywall>
    </InspoWgRequestProvider>
  );
}
